import { useTheme, useMediaQuery } from "@mui/material";
import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Button,
  TextField,
  Box,
  Alert,
  Snackbar,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  FormHelperText,
} from "@mui/material";

import {
  alphanumericWithSpaceValidation,
  validateEmail,
  validateField,
  zipCodeValidation,
} from "../../common/helpers/validation";
import { CircleOutlined } from "@mui/icons-material";
import {
  ModelYear,
  Rental,
  comp,
  vehBodyType,
  Roadside,
} from "../../common/StaticFieldList";
import {
  createVehicleInfo,
  getAllVehicleInfo,
  getVehicleInfoByVIN,
  updateVehicleInfo,
  getVehicleInfoActionByYmsYears,
  getVehicleInfoActionByYmsModels,
  getVehicleInfoActionByYmsMakes,
  getVehicleInfoActionByYmsVins,
  getVehicleInfoActionByYmsStyles,
} from "../../redux/actions/vehicleInfoActions";
import { populateValue } from "../../common/helpers/utils";

const requiredFields = [
  "vin",
  "manufacturer",
  "model",
  "vehBodyType",
  "modelYear",
  "zipcode",
];

const validationRules = [
  { fieldName: "vin", label: "VIN", type: "text", apiCall: true, id: "vin" },
  {
    fieldName: "modelYear",
    label: "Model Year",
    type: "select",
    option: ModelYear,
    id: "modelyear",
  },

  {
    fieldName: "manufacturer",
    label: "Make",
    id: "manufacturer",
  },
  { fieldName: "model", label: "Model", id: "model" },
  {
    fieldName: "vehBodyType",
    label: "Body Type",
    // type: "select",
    // option: vehBodyType,
    id: "vehbodytype",
  },
  {
    fieldName: "zipcode",
    label: "Garaging Zip code",
    type: "number",
    id: "zipcode",
  },
];

const validationRulesGeneral = [
  {
    fieldName: "comp",
    label: "Comp",
    type: "select",
    option: comp,
    id: "comp",
  },
  {
    fieldName: "coll",
    label: "Coll",
    type: "select",
    option: comp,
    id: "coll",
  },
  {
    fieldName: "roadside",
    label: "Roadside",
    type: "select",
    option: Roadside,
    id: "roadside",
  },
  {
    fieldName: "rental",
    label: "Rental",
    type: "select",
    option: Rental,
    id: "rental",
  },
  {
    fieldName: "tow",
    label: "Tow",
    type: "select",
    option: Roadside,
    id: "tow",
  },
];

const validationFunctions = {
  firstName: alphanumericWithSpaceValidation,
  lastName: alphanumericWithSpaceValidation,
  email: validateEmail,
  zipcode: zipCodeValidation,
};

const VehicleSave = forwardRef(({
  setShowHideVehicleForm,
  vehicleData,
  vehicleAttributeFormData,
  backData,
  handleNext,
  handleNextSavingData,
  handleBack,
  showVehicle,
  handleSubmitVehicleInfo,
  handleVehicleData,
  handleForward,
  searchQuoteId,
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const dispatch = useDispatch();
  const getGpId = useSelector((state) => state.generalPartyInfo.gpId);
  let quoteId = getGpId;
  const [selectedOption, setSelectedOption] = React.useState(
    vehicleData.entryMethod || "vin"
  );

  const years = useSelector((state) => state.vehicleInfo.years);
  const Make = useSelector((state) => state.vehicleInfo.makes);
  const models = useSelector((state) => state.vehicleInfo.models);
  const vehBodyType = useSelector((state) => state.vehicleInfo.styles);
  const vins = useSelector((state) => state.vehicleInfo.vins);
  const [isDirty, setIsDirty] = useState(false);
  // Add state to track auto-populated fields
  const [autoPopulatedFields, setAutoPopulatedFields] = useState({
    manufacturer: false,
    model: false,
    modelYear: false,
    vehBodyType: false,
  });
  const validationRulesyms = [
    {
      fieldName: "modelYear",
      label: "Model Year",
      type: "select",
      option: years,
      id: "modelyear",
    },

    {
      fieldName: "manufacturer",
      label: "Make",
      type: "select",
      option: Make,
      id: "manufacturer",
    },
    {
      fieldName: "model",
      label: "Model",
      type: "select",
      option: models,
      id: "model",
    },
    {
      fieldName: "vehBodyType",
      label: "Body Type",
      type: "select",
      option: vehBodyType,
      id: "vehbodytype",
    },
    {
      fieldName: "vin",
      label: "VIN",
      type: "select",
      option: vins,
      apiCall: true,
      id: "vin",
    },
    {
      fieldName: "zipcode",
      label: "Garaging Zip code",
      type: "number",
      id: "zipcode",
    },
  ];

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const loading = useSelector((state) => state.loading.CREATE_USER);

  const newFieldArg = [
    ...validationRules,
    ...validationRulesGeneral,
    validationRulesyms,
  ];

  const generalPartyInfo = useSelector(
    (state) => state.generalPartyInfo.allGeneralPartyInfo
  );
  const zip = generalPartyInfo.zip;
  const [formState, setFormState] = useState({
    formData: {
      ...newFieldArg.reduce(
        (fields, { fieldName }) => ({
          ...fields,
          [fieldName]: "",
        }),
        {}
      ),
      zipcode: zip || "",
    },
    touchedFields: newFieldArg.reduce(
      (fields, { fieldName }) => ({
        ...fields,
        [fieldName]: false,
      }),
      {}
    ),
    fieldErrors: newFieldArg.reduce(
      (errors, { fieldName }) => ({
        ...errors,
        [fieldName]: "",
      }),
      {}
    ),
  });
  const { formData, touchedFields, fieldErrors } = formState;
  const [formDatas, setFormDatas] = useState({
    modelYear: vehicleData?.modelYear || "",
    manufacturer: vehicleData?.manufacturer || "",
    model: vehicleData?.model || "",
    vehBodyType: vehicleData?.vehBodyType || "",
    vin: vehicleData?.vin || "",
    zipcode: vehicleData?.zipcode || zip,
  });

  useEffect(() => {
    if (zip) {
      setFormState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          zipcode: zip,
        },
      }));

      setFormDatas((prev) => ({
        ...prev,
        zipcode: zip,
      }));
    }
  }, [zip]);

  useEffect(() => {
    dispatch(getVehicleInfoActionByYmsYears());
  }, [dispatch]);

  useEffect(() => {
    if (formDatas.modelYear) {
      dispatch(getVehicleInfoActionByYmsMakes(formDatas.modelYear));
      setFormDatas((prev) => ({
        ...prev,
        manufacturer: "",
        model: "",
        vehBodyType: "",
        vin: "",
      }));
    }
  }, [formDatas.modelYear, dispatch]);

  useEffect(() => {
    if (formDatas.modelYear && formDatas.manufacturer) {
      dispatch(
        getVehicleInfoActionByYmsModels(
          formDatas.modelYear,
          formDatas.manufacturer
        )
      );
      setFormDatas((prev) => ({
        ...prev,
        model: "",
        vehBodyType: "",
        vin: "",
      }));
    }
  }, [formDatas.manufacturer, formDatas.modelYear, dispatch]);

  useEffect(() => {
    if (formDatas.modelYear && formDatas.manufacturer && formDatas.model) {
      dispatch(
        getVehicleInfoActionByYmsStyles(
          formDatas.modelYear,
          formDatas.manufacturer,
          formDatas.model
        )
      );
      setFormDatas((prev) => ({
        ...prev,
        vehBodyType: "",
        vin: "",
      }));
    }
  }, [formDatas.model, formDatas.manufacturer, formDatas.modelYear, dispatch]);

  useEffect(() => {
    if (
      formDatas.modelYear &&
      formDatas.manufacturer &&
      formDatas.model &&
      formDatas.vehBodyType
    ) {
      dispatch(
        getVehicleInfoActionByYmsVins(
          formDatas.modelYear,
          formDatas.manufacturer,
          formDatas.model,
          formDatas.vehBodyType
        )
      );
    }
  }, [
    formDatas.vehBodyType,
    formDatas.model,
    formDatas.manufacturer,
    formDatas.modelYear,
    dispatch,
  ]);

  const [touchedFieldyms, setTouchedFieldyms] = useState({});

  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);

    if (event.target.value === "vin" && formState.formData) {
      setFormDatas({
        modelYear: formState.formData.modelYear || "",
        manufacturer: formState.formData.manufacturer || "",
        model: formState.formData.model || "",
        vehBodyType: formState.formData.vehBodyType || "",
        vin: formState.formData.vin || "",
        zipcode: formState.formData.zipcode || zip,
      });
    } else if (event.target.value === "yms" && formDatas) {
      // Reset auto-populated fields when switching to YMS
    setAutoPopulatedFields({
      manufacturer: false,
      model: false,
      modelYear: false,
      vehBodyType: false,
    });
      setFormState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          modelYear: formDatas.modelYear,
          manufacturer: formDatas.manufacturer,
          model: formDatas.model,
          vehBodyType: formDatas.vehBodyType,
          vin: formDatas.vin,
          zipcode: formDatas.zipcode || zip || "",
        },
      }));
    }
  };

  const handleInputChangeyms = (e) => {
    const { name, value } = e.target;
    setIsDirty(true);
    setFormDatas((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "modelYear") {
      setFormDatas((prev) => ({
        ...prev,
        manufacturer: "",
        model: "",
        vehBodyType: "",
        vin: "",
      }));
    } else if (name === "manufacturer") {
      setFormDatas((prev) => ({
        ...prev,
        model: "",
        vehBodyType: "",
        vin: "",
      }));
    } else if (name === "model") {
      setFormDatas((prev) => ({
        ...prev,
        vehBodyType: "",
        vin: "",
      }));
    } else if (name === "vehBodyType") {
      setFormDatas((prev) => ({
        ...prev,
        vin: "",
      }));
    }
  };

  const saveWithoutNav = async () => {
   try {
     const dataToSubmit = selectedOption === "vin" ? formData : formDatas;
     const combinedFormData = {
       ...(selectedOption === "yms"
         ? {
             ...formDatas,
             comp: formData.comp,
             coll: formData.coll,
             roadside: formData.roadside,
             rental: formData.rental,
             tow: formData.tow,
           }
         : formData),
       gpId: getGpId ? getGpId : searchQuoteId,
       entryMethod: selectedOption,
     };

     if (vehicleData?.id) {
       await dispatch(updateVehicleInfo({ ...combinedFormData, id: vehicleData.id }, () => {}));
     } else {
       await dispatch(createVehicleInfo(combinedFormData, () => {}));
     }
     setIsDirty(false);
     return { ok: true };
   } catch (e) {
     return { ok: false, error: e };
   }
 };


  useImperativeHandle(ref, () => ({
    shouldConfirm: () => isDirty,
    saveWithoutNav,
    resetForm,
  }));

const resetForm = () => {
    // restore to initial state (empty  zip)
    setFormState((prev) => ({
      ...prev,
      formData: {
        ...Object.keys(prev.formData).reduce((acc, k) => ({ ...acc, [k]: "" }), {}),
        zipcode: zip || "",
      },
    }));
    setFormDatas({
      modelYear: "",
      manufacturer: "",
      model: "",
      vehBodyType: "",
      vin: "",
      zipcode: zip || "",
    });
    setIsDirty(false);
  };

  const handleBluryms = (fieldName) => {
    setTouchedFieldyms((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setIsDirty(true);
    setFormState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  const handleVinPopulate = async (value) => {
    if (!value) {
      setFormState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          manufacturer: "",
          model: "",
          modelYear: "",
        },
      }));
      // Reset auto-populated fields state when VIN is cleared
      setAutoPopulatedFields({
        manufacturer: false,
        model: false,
        modelYear: false,
        vehBodyType: false,
      });
      return;
    }

    const response = await dispatch(getVehicleInfoByVIN(value, ""));
    if (response) {
      setFormState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          manufacturer: response.make,
          model: response.model,
          modelYear: response.modelYear,
          vehBodyType: response.bodyType,
        },
      }));
      // Mark fields as auto-populated
      setAutoPopulatedFields({
        manufacturer: !!response.make,
        model: !!response.model,
        modelYear: !!response.modelYear,
        vehBodyType: !!response.bodyType,
      });
    } else {
      handleSnackbarOpen(
        `The requested VIN "${value}" is too short. VINs should be at least 5 characters long, with most modern VINs being 11-17 characters long. The requested VIN is 2 characters`,
        "error"
      );
      // Reset auto-populated fields state on error
      setAutoPopulatedFields({
        manufacturer: false,
        model: false,
        modelYear: false,
        vehBodyType: false,
      });
    }
  };
  const isFieldDisabled = (fieldName) => {
    return selectedOption === "vin" && autoPopulatedFields[fieldName];
  };
  const handleBlur = (field) => {
    if (field === "vin") {
      handleVinPopulate(formData[field]);
    }
    setFormState((prevFormState) => ({
      ...prevFormState,
      touchedFields: {
        ...prevFormState.touchedFields,
        [field]: true,
      },
    }));

    if (!requiredFields.includes(field)) {
      return;
    }

    const value = formData[field];
    const error = validateField(
      value,
      newFieldArg.find((rule) => rule.fieldName === field).label
    );

    const additionalError = validationFunctions[field]
      ? validationFunctions[field](value)
      : "";

    setFormState((prevFormState) => ({
      ...prevFormState,
      fieldErrors: {
        ...prevFormState.fieldErrors,
        [field]: error || additionalError,
      },
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar((prevSnackbar) => ({
      ...prevSnackbar,
      open: false,
    }));
  };

  const handleSnackbarOpen = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleFormSubmit = async(event) => {
    event.preventDefault();
    const combinedFormData = {
      ...(selectedOption === "yms"
        ? {
            ...formDatas,
            comp: formData.comp,
            coll: formData.coll,
            roadside: formData.roadside,
            rental: formData.rental,
            tow: formData.tow,
          }
        : formData),
      entryMethod: selectedOption,
      gpId: quoteId ? quoteId : searchQuoteId,
    };

handleSubmitVehicleInfo(combinedFormData);
if(vehicleAttributeFormData)
{
  handleNextSavingData(vehicleAttributeFormData)
}
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const dataToSubmit = selectedOption === "vin" ? formData : formDatas;

    const fieldsToValidate =
      selectedOption === "vin" ? validationRules : validationRulesyms;

    const validationErrors = fieldsToValidate.reduce(
      (errors, { fieldName, label }) => {
        if (requiredFields.includes(fieldName)) {
          const value = dataToSubmit[fieldName];
          const error = validateField(value, label);

          const additionalError = validationFunctions[fieldName]
            ? validationFunctions[fieldName](value)
            : "";

          setFormState((prevFormState) => ({
            ...prevFormState,
            fieldErrors: {
              ...prevFormState.fieldErrors,
              [fieldName]: error || additionalError,
            },
          }));

          return {
            ...errors,
            [fieldName]: error || additionalError,
          };
        }
        return errors;
      },
      {}
    );

    const hasErrors = Object.values(validationErrors).some((error) => !!error);

    if (!hasErrors) {
      try {
        const combinedFormData = {
          ...(selectedOption === "yms"
            ? {
                ...formDatas,
                comp: formData.comp,
                coll: formData.coll,
                roadside: formData.roadside,
                rental: formData.rental,
                tow: formData.tow,
              }
            : formData),

          gpId: quoteId ? quoteId : searchQuoteId,
          entryMethod: selectedOption,
        };

        if (vehicleData.id) {
          combinedFormData.id = vehicleData.id;
          await dispatch(
            updateVehicleInfo(combinedFormData, getCreateUserSuccess)
          );
        } else {
          await dispatch(
            createVehicleInfo(combinedFormData, getCreateUserSuccess)
          );
        }
      } catch (error) {
        handleSnackbarOpen(
          error.message || "Vehicle service failed. Please try again.",
          "error"
        );
      }
    } else {
      handleSnackbarOpen(
        "Please fill in all required fields correctly.",
        "error"
      );
    }
    handleNext();
  };

  const getCreateUserSuccess = async () => {
    handleSnackbarOpen("Vehicle created successfully.", "success");
    setIsDirty(false);
    setShowHideVehicleForm(false);
    await dispatch(getAllVehicleInfo(quoteId || searchQuoteId));

    setFormState({
      formData: newFieldArg.reduce(
        (fields, { fieldName }) => ({
          ...fields,
          [fieldName]: "",
        }),
        {}
      ),
      touchedFields: newFieldArg.reduce(
        (fields, { fieldName }) => ({
          ...fields,
          [fieldName]: false,
        }),
        {}
      ),
      fieldErrors: newFieldArg.reduce(
        (errors, { fieldName }) => ({
          ...errors,
          [fieldName]: "",
        }),
        {}
      ),
    });
  };

  useEffect(() => {
    if (vehicleData?.id) {
      setFormState((prevFormState) => ({
        ...prevFormState,
        formData: newFieldArg.reduce(
          (fields, { fieldName }) => ({
            ...fields,
            [fieldName]:
              (vehicleData &&
                populateValue(
                  vehicleData[fieldName],
                  fieldName,
                  newFieldArg
                )) ||
              "",
          }),
          {}
        ),
      }));

      setFormDatas((prevFormDatas) => ({
        ...prevFormDatas,
        modelYear: vehicleData.modelYear || "",
        manufacturer: vehicleData.manufacturer || "",
        model: vehicleData.model || "",
        vehBodyType: vehicleData.vehBodyType || "",
        vin: vehicleData.vin || "",
        zipcode: vehicleData.zipcode || zip || "",
      }));

      setFormState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          zipcode: vehicleData.zipcode || zip || "",
        },
      }));
    }
      else {
      setFormState((prevFormState) => ({
        ...prevFormState,
        formData: newFieldArg.reduce(
          (fields, { fieldName }) => ({
            ...fields,
            [fieldName]:
              (backData &&
                populateValue(
                  backData[fieldName],
                  fieldName,
                  newFieldArg
                )) ||
              "",
          }),
          {}
        ),
      }));

      setFormDatas((prevFormDatas) => ({
        ...prevFormDatas,
        modelYear: backData.modelYear || "",
        manufacturer: backData.manufacturer || "",
        model: backData.model || "",
        vehBodyType: backData.vehBodyType || "",
        vin: backData.vin || "",
        zipcode: backData.zipcode || zip || "",
      }));

      setFormState((prevState) => ({
        ...prevState,
        formData: {
          ...prevState.formData,
          zipcode: backData.zipcode || zip || "",
        },
      }));
    }
  }, [vehicleData,backData, zip]);

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        "@media (min-width: 768px) and (max-width: 1024px)": {
          minHeight: "120vh",
          height: "auto",
          overflowY: "auto",
        },
        "@media (min-width: 1024px) and (min-height: 1366px)": {
          height: "auto",
          minHeight: "130vh",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "36px",
          maxWidth: "100%",
          padding: isMobile ? "0 10px" : "0",
        }}
      >
        <Box
          id="form"
          component="form"
          sx={{
            width: isMobile ? "100%" : isTablet ? "90%" : "853px",
            borderRadius: "4px",
            backgroundColor: "#fff",
            border: "1px solid #e3e3e3",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            padding: "30px 0px 24px",
            gap: "23px",
            maxWidth: "100%",
          }}
        >
          <Typography
            id="vehicleinformation"
            variant="h1"
            component="div"
            sx={{
              backgroundColor: "transparent",
              alignSelf: "stretch",
              height: "51px",
              fontFamily: '"Poppins", sans-serif',
              fontWeight: "600",
              fontSize: "25px",
              color: "#242929",
              paddingLeft: isMobile ? "20px" : "50px",
              display: "flex",
              alignItems: "center",
            }}
          >
            Vehicle Information
          </Typography>
          <hr style={{ borderTop: "1px solid #ccc", width: "100%" }} />
          <Box
            sx={{
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "flex-end",
              padding: isMobile ? "0px 20px" : "0px 44px 0px 52px",
              boxSizing: "border-box",
              maxWidth: "100%",
            }}
          >
            <Box
              sx={{
                flex: "1",
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "16px",
                maxWidth: "100%",
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              <Box
                sx={{
                  flex: "1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  gap: "4px",
                  minWidth: isMobile ? "100%" : "240px",
                  maxWidth: "100%",
                }}
              >
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    row
                    name="search-option"
                    value={selectedOption}
                    onChange={handleOptionChange}
                    sx={{
                      marginBottom: "16px",
                      flexWrap: isMobile ? "wrap" : "nowrap",
                    }}
                  >
                    <FormControlLabel
                      value="vin"
                      control={
                        <Radio
                          sx={{
                            color: "black",
                            "&.Mui-checked": {
                              color: "black",
                            },
                          }}
                        />
                      }
                      label="VIN"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontFamily: '"Poppins", sans-serif',
                          fontWeight: "500",
                        },
                      }}
                    />
                    <FormControlLabel
                      value="yms"
                      control={
                        <Radio
                          sx={{
                            color: "black",
                            "&.Mui-checked": {
                              color: "black",
                            },
                          }}
                        />
                      }
                      label="YMS"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          fontFamily: '"Poppins", sans-serif',
                          fontWeight: "500",
                        },
                      }}
                    />
                  </RadioGroup>
                </FormControl>
                <Box
                  sx={{
                    alignSelf: "stretch",
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: "5px 20px",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    padding: "0px 0px 8px",
                  }}
                >
                  {selectedOption === "vin"
                    ? validationRules.map(
                        ({ fieldName, label, type, option }, index) => (
                          <Box
                            key={index}
                            sx={{
                              width: "100%",
                              margin: "0 0 15px 0",
                              display: "flex",
                              flexFlow: "column",
                            }}
                          >
                            <Typography
                              key={fieldName}
                              variant="body1"
                              sx={{
                                position: "relative",
                                lineHeight: "40px",
                                fontWeight: "500",
                                display: "inline-block",
                                minWidth: "83px",
                                zIndex: "1",
                                fontFamily: '"Poppins", sans-serif',
                              }}
                            >
                              {label}
                              {requiredFields.includes(fieldName) && (
                                <span
                                  style={{ color: "red", marginLeft: "2px" }}
                                >
                                  *
                                </span>
                              )}
                            </Typography>
                            {type === "select" ? (
                              <FormControl
                                fullWidth
                                variant="outlined"
                                sx={{
                                  border: "none",
                                  backgroundColor: "transparent",
                                  alignSelf: "stretch",
                                  fontFamily: '"Poppins", sans-serif',
                                  fontWeight: "500",
                                  fontSize: "16px",
                                  zIndex: "1",
                                }}
                              >
                                <Select
                                  native
                                  name={fieldName}
                                  value={formData[fieldName] || ""}
                                  disabled={isFieldDisabled(fieldName)} // Add this line
                                  onChange={(e) => {
                                    handleInputChange(e);
                                  }}
                                  onBlur={() => handleBlur(fieldName)}
                                  error={
                                    touchedFields[fieldName] &&
                                    !!fieldErrors[fieldName]
                                  }
                                  displayEmpty
                                  inputProps={{ "aria-label": label }}
                                  sx={{
                                    height: "52px",
                                    "& .MuiInputBase-root": {
                                      height: "52px",
                                      backgroundColor: isFieldDisabled(
                                        fieldName
                                      )
                                        ? "#f5f5f5"
                                        : "#fff", // Gray background for disabled fields
                                      fontSize: "14px",
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                    "& .MuiInputBase-input": {
                                      color: isFieldDisabled(fieldName)
                                        ? "#666"
                                        : "#242929", // Lighter text for disabled fields
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                    "& fieldset": {
                                      borderColor:
                                        touchedFields[fieldName] &&
                                        !!fieldErrors[fieldName]
                                          ? "#d32f2f"
                                          : "#C1C1C1",
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                  }}
                                >
                                  <option value="" disabled>
                                    Select {label}
                                  </option>
                                  {option &&
                                    option.map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                </Select>
                                {touchedFields[fieldName] &&
                                  fieldErrors[fieldName] && (
                                    <FormHelperText error>
                                      {fieldErrors[fieldName]}
                                    </FormHelperText>
                                  )}
                              </FormControl>
                            ) : (
                              <TextField
                                fullWidth
                                type={type}
                                variant="outlined"
                                required={requiredFields.includes(fieldName)}
                                id={fieldName}
                                placeholder={label}
                                name={fieldName}
                                autoComplete={fieldName}
                                value={formData[fieldName]}
                                disabled={isFieldDisabled(fieldName)} // Add this line
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (
                                    type === "text" &&
                                    e.target.value.length > 20
                                  ) {
                                    return;
                                  }
                                  if (
                                    fieldName === "vin" &&
                                    !/^[A-Za-z0-9]*$/.test(e.target.value)
                                  ) {
                                    return;
                                  }
                                  if (fieldName === "zipcode") {
                                    if (!/^\d*$/.test(val)) {
                                      return;
                                    }
                                    if (val.length > 9) {
                                      return;
                                    }
                                  }
                                  if (
                                    fieldName === "model" &&
                                    (!/^[A-Za-z0-9 ]*$/.test(val) ||
                                      val.length > 50)
                                  ) {
                                    return;
                                  }
                                  handleInputChange(e);
                                }}
                                onBlur={() => handleBlur(fieldName)}
                                error={
                                  touchedFields[fieldName] &&
                                  !!fieldErrors[fieldName]
                                }
                                helperText={
                                  touchedFields[fieldName]
                                    ? fieldErrors[fieldName]
                                    : ""
                                }
                                sx={{
                                  "& fieldset": {
                                    borderColor: "#C1C1C1",
                                    fontFamily: '"Poppins", sans-serif',
                                  },
                                  "& .MuiInputBase-root": {
                                    height: "52px",
                                    fontFamily: '"Poppins", sans-serif',
                                    backgroundColor: isFieldDisabled(fieldName)
                                      ? "#f5f5f5"
                                      : "#fff", // Gray background for disabled fields
                                    fontSize: "16px",
                                  },
                                  "& .MuiInputBase-input": {
                                    color: isFieldDisabled(fieldName)
                                      ? "#666"
                                      : "#242929", // Lighter text for disabled fields
                                    fontFamily: '"Poppins", sans-serif',
                                  },
                                }}
                              />
                            )}
                          </Box>
                        )
                      )
                    : validationRulesyms.map(
                        ({ fieldName, label, type, option }, index) => (
                          <Box
                            key={index}
                            sx={{
                              width: "100%",
                              margin: "0 0 15px 0",
                              display: "flex",
                              flexFlow: "column",
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                position: "relative",
                                lineHeight: "40px",
                                fontWeight: "500",
                                display: "inline-block",
                                minWidth: "83px",
                                zIndex: "1",
                                fontFamily: '"Poppins", sans-serif',
                              }}
                            >
                              {label}
                              {requiredFields.includes(fieldName) && (
                                <span
                                  style={{ color: "red", marginLeft: "2px" }}
                                >
                                  *
                                </span>
                              )}
                            </Typography>
                            {type === "select" ? (
                              <FormControl
                                fullWidth
                                variant="outlined"
                                sx={{
                                  border: "none",
                                  backgroundColor: "transparent",
                                  alignSelf: "stretch",
                                  fontFamily: '"Poppins", sans-serif',
                                  fontWeight: "500",
                                  fontSize: "16px",
                                  zIndex: "1",
                                }}
                              >
                                <Select
                                  native
                                  name={fieldName}
                                  value={formDatas[fieldName] || ""}
                                  onChange={handleInputChangeyms}
                                  onBlur={() => handleBluryms(fieldName)}
                                  error={
                                    touchedFields[fieldName] &&
                                    !!fieldErrors[fieldName]
                                  }
                                  displayEmpty
                                  inputProps={{ "aria-label": label }}
                                  disabled={
                                    (fieldName === "manufacturer" &&
                                      !formDatas.modelYear) ||
                                    (fieldName === "model" &&
                                      !formDatas.manufacturer) ||
                                    (fieldName === "vehBodyType" &&
                                      !formDatas.model) ||
                                    (fieldName === "vin" &&
                                      !formDatas.vehBodyType)
                                  }
                                  sx={{
                                    height: "52px",
                                    "& .MuiInputBase-root": {
                                      height: "52px",
                                      backgroundColor: "#fff",
                                      fontSize: "14px",
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                    "& .MuiInputBase-input": {
                                      color: "#242929",
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                    "& fieldset": {
                                      borderColor:
                                        touchedFields[fieldName] &&
                                        !!fieldErrors[fieldName]
                                          ? "#d32f2f"
                                          : "#C1C1C1",
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                  }}
                                >
                                  <option value="" disabled>
                                    Select {label}
                                  </option>
                                  {Array.isArray(option) &&
                                    option.map((value) => (
                                      <option key={value} value={value}>
                                        {value}
                                      </option>
                                    ))}
                                </Select>
                                {touchedFields[fieldName] &&
                                  fieldErrors[fieldName] && (
                                    <FormHelperText error>
                                      {fieldErrors[fieldName]}
                                    </FormHelperText>
                                  )}
                              </FormControl>
                            ) : (
                              <TextField
                                fullWidth
                                type={type}
                                variant="outlined"
                                id={fieldName}
                                placeholder={label}
                                name={fieldName}
                                autoComplete={fieldName}
                                value={formDatas[fieldName]}
                                onChange={handleInputChangeyms}
                                onBlur={() => handleBluryms(fieldName)}
                                error={
                                  touchedFields[fieldName] &&
                                  !!fieldErrors[fieldName]
                                }
                                helperText={
                                  touchedFields[fieldName]
                                    ? fieldErrors[fieldName]
                                    : ""
                                }
                                disabled={
                                  (fieldName === "manufacturer" &&
                                    !formDatas.modelYear) ||
                                  (fieldName === "model" &&
                                    !formDatas.manufacturer) ||
                                  (fieldName === "vehBodyType" &&
                                    !formDatas.model) ||
                                  (fieldName === "vin" &&
                                    !formDatas.vehBodyType)
                                }
                                sx={{
                                  "& fieldset": { borderColor: "#C1C1C1" },
                                  "& .MuiInputBase-root": {
                                    height: "52px",
                                    backgroundColor: "#fff",
                                    fontSize: "16px",
                                    fontFamily: '"Poppins", sans-serif',
                                  },
                                  "& .MuiInputBase-input": {
                                    color: "#242929",
                                    fontFamily: '"Poppins", sans-serif',
                                  },
                                }}
                              />
                            )}
                          </Box>
                        )
                      )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Typography
            id="coverages"
            variant="h1"
            component="div"
            sx={{
              backgroundColor: "transparent",
              alignSelf: "stretch",
              height: "51px",
              fontFamily: '"Poppins", sans-serif',
              fontWeight: "600",
              fontSize: "25px",
              color: "#242929",
              paddingLeft: isMobile ? "20px" : "50px",
              display: "flex",
              alignItems: "center",
            }}
          >
            Coverages
          </Typography>
          <hr style={{ borderTop: "1px solid #ccc", width: "100%" }} />
          <Box
            sx={{
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "flex-end",
              padding: isMobile ? "0px 20px" : "0px 44px 0px 52px",
              boxSizing: "border-box",
              maxWidth: "100%",
            }}
          >
            <Box
              sx={{
                flex: "1",
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                gap: "16px",
                maxWidth: "100%",
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              <Box
                sx={{
                  flex: "1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  gap: "4px",
                  minWidth: isMobile ? "100%" : "240px",
                  maxWidth: "100%",
                  fontFamily: '"Poppins", sans-serif',
                }}
              >
                <Box
                  sx={{
                    alignSelf: "stretch",
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                    gap: "5px 20px",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    padding: "0px 0px 8px",
                  }}
                >
                  {validationRulesGeneral.map(
                    ({ fieldName, label, type, option }, index) => (
                      <Box
                        key={index}
                        sx={{
                          width: "100%",
                          margin: "0 0 15px 0",
                          display: "flex",
                          flexFlow: "column",
                        }}
                      >
                        <Typography
                          key={fieldName}
                          variant="body1"
                          sx={{
                            position: "relative",
                            lineHeight: "40px",
                            fontWeight: "500",
                            display: "inline-block",
                            minWidth: "83px",
                            zIndex: "1",
                            fontFamily: '"Poppins", sans-serif',
                          }}
                        >
                          {label}
                        </Typography>
                        {type === "select" ? (
                          <FormControl
                            fullWidth
                            variant="outlined"
                            sx={{
                              border: "none",
                              backgroundColor: "transparent",
                              alignSelf: "stretch",
                              fontFamily: '"Poppins", sans-serif',
                              fontWeight: "500",
                              fontSize: "16px",
                              zIndex: "1",
                            }}
                          >
                            <Select
                              native
                              name={fieldName}
                              value={formData[fieldName] || ""}
                              onChange={(e) => handleInputChange(e)}
                              error={
                                touchedFields[fieldName] &&
                                !!fieldErrors[fieldName]
                              }
                              displayEmpty
                              required={requiredFields.includes(fieldName)}
                              id={fieldName}
                              inputProps={{ "aria-label": label }}
                              sx={{
                                height: "52px",
                                "& .MuiInputBase-root": {
                                  height: "52px",
                                  backgroundColor: "#fff",
                                  fontSize: "14px",
                                  fontFamily: '"Poppins", sans-serif',
                                },
                                "& .MuiInputBase-input": {
                                  color: "#242929",
                                  fontFamily: '"Poppins", sans-serif',
                                },
                                "& fieldset": {
                                  borderColor:
                                    touchedFields[fieldName] &&
                                    !!fieldErrors[fieldName]
                                      ? "#d32f2f"
                                      : "#C1C1C1",
                                  fontFamily: '"Poppins", sans-serif',
                                },
                              }}
                            >
                              <option value="" disabled>
                                Select {label}
                              </option>
                              {option &&
                                option.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                            </Select>
                            {touchedFields[fieldName] &&
                              fieldErrors[fieldName] && (
                                <FormHelperText error>
                                  {fieldErrors[fieldName]}
                                </FormHelperText>
                              )}
                          </FormControl>
                        ) : (
                          <TextField
                            fullWidth
                            type={type}
                            variant="outlined"
                            required={requiredFields.includes(fieldName)}
                            id={fieldName}
                            placeholder={label}
                            name={fieldName}
                            autoComplete={fieldName}
                            value={formData[fieldName]}
                            onChange={(e) => handleInputChange(e)}
                            error={
                              touchedFields[fieldName] &&
                              !!fieldErrors[fieldName]
                            }
                            helperText={
                              touchedFields[fieldName]
                                ? fieldErrors[fieldName]
                                : ""
                            }
                            sx={{
                              "& fieldset": {
                                borderColor: "#C1C1C1",
                                fontFamily: '"Poppins", sans-serif',
                              },
                              "& .MuiInputBase-root": {
                                height: "52px",
                                backgroundColor: "#fff",
                                fontSize: "16px",
                                fontFamily: '"Poppins", sans-serif',
                              },
                              "& .MuiInputBase-input": {
                                color: "#242929",
                                fontFamily: '"Poppins", sans-serif',
                              },
                            }}
                          />
                        )}
                      </Box>
                    )
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {showVehicle && <></>}
          {!showVehicle ? (
            <>
              <Box
                sx={{
                  width: "222px",
                  height: "42px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  gap: "14px",
                  marginRight: isMobile ? "20px" : "50px",
                }}
              >
                <Button
                  id="backbutton"
                  style={{
                    flex: "none",
                    width: "auto",
                    cursor: "pointer",
                    zIndex: "1",
                  }}
                  startIcon={<ArrowBackIosIcon />}
                  disableElevation={true}
                  variant="outlined"
                    onClick={() => setShowHideVehicleForm(false)}
                >
                  Cancel
                </Button>

                <Button
                  id="additionalinfo"
                  endIcon={<ArrowForwardIosIcon />}
                  disableElevation={true}
                  variant="outlined"
                  onClick={handleFormSubmit}
                >
                  Next
                </Button>
              </Box>
              <Box
                sx={{
                  alignSelf: "stretch",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  padding: isMobile ? "20px 20px 50px" : "20px 20px 50px 23px",
                  fontFamily: '"Poppins", sans-serif',
                }}
              >
                {/* <Button
                  id="continue"
                  disableElevation={true}
                  variant="contained"
                  className="btn-primary"
                  sx={{
                    textTransform: "none",
                    color: "#fff",
                    fontSize: "16px",
                    background: "#077BFD",
                    borderRadius: "4px",
                    fontFamily: '"Poppins", sans-serif',
                    "&:hover": { background: "#077BFD" },
                    width: 140,
                    height: 42,
                  }}
                  onClick={handleSubmit}
                >
                  Continue
                </Button> */}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
                paddingRight: isMobile ? "20px" : "50px",
              }}
            >
              <Button
                id="cancel"
                sx={{
                  mt: 3,
                  mb: 2,
                  marginRight: "1rem",
                  fontFamily: '"Poppins", sans-serif',
                }}
                onClick={() => setShowHideVehicleForm(false)}
                className="btn-secondary"
              >
                Cancel
              </Button>

            </Box>
          )}
        </Box>

        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
});

export default VehicleSave;
